import { useCallback, useEffect, useRef, useState } from 'react'

// How often to re-check a job that is still generating.
const DEFAULT_POLL_MS = 10000

/**
 * useJobStatus — the status-then-poll state machine behind a button that launches a
 * long-running server-side job (status on mount → poll while a run is in flight →
 * settle on its outcome).
 *
 * The core shared by useReportJob (which adds opening the finished PDF) and the
 * Control Panel's manual insight run. The job-specific parts are injected as two
 * async callbacks, so the hook knows nothing about which endpoint it drives:
 *
 *   fetchStatus() → { status: 'none'|'generating'|'done'|'error', message? }
 *   launch()      → starts a run (resolves on 202, not on completion)
 *
 * `identity` is a stable string for the thing the job is about. The settled outcome
 * is tagged with the identity it settled for and `status`/`error` are DERIVED: until
 * an outcome for the *current* identity lands (or while disabled) the machine reads
 * 'unknown'. So a consumer that outlives one subject (a detail *page* that swaps
 * subjects without remounting) needs no synchronous reset — the same tag-and-derive
 * idiom CaseDetails uses across :id changes — and a modal that remounts per subject
 * simply passes its stable per-mount identity.
 *
 * `enabled` gates all of it: when false (no subject, or the viewer lacks the right
 * to launch) the hook makes no calls and reports 'unknown'. Hooks can't be called
 * conditionally, so the consumer always calls this and toggles `enabled` instead.
 *
 * `failedMessage` / `launchFailedMessage` are the fallbacks shown when the server
 * gives no message of its own.
 *
 * @returns {{ status: string, error: string|null, start: () => Promise<void>,
 *             settle: (status: string, error?: string|null) => void }}
 *   status — 'unknown' until the first status settles, then none/generating/done/error
 *   error  — a message to surface, or null
 *   start  — launch a run (flips to 'generating' at once; the poll takes over)
 *   settle — for a wrapper that learns the outcome some other way (e.g. a 404 on the
 *            finished artifact) to record it against the current identity
 */
export function useJobStatus({
  enabled = true,
  identity,
  fetchStatus,
  launch,
  pollMs = DEFAULT_POLL_MS,
  failedMessage = 'The job failed.',
  launchFailedMessage = 'Could not start the job.',
}) {
  // Latest callbacks kept in a ref (synced in an effect, never during render) so the
  // status/poll effects can depend on `identity` alone, not on the fresh closures the
  // consumer passes each render — otherwise every render would re-fire a status fetch.
  const fnsRef = useRef({ fetchStatus, launch })
  useEffect(() => {
    fnsRef.current = { fetchStatus, launch }
  })

  // The settled outcome, tagged with the identity it settled for. Never reset
  // synchronously — `status`/`error` below derive 'unknown'/null whenever the tag
  // doesn't match the current identity, which covers both "not yet loaded" and
  // "subject just changed".
  const [settled, setSettled] = useState({ identity: null, status: 'none', error: null })

  const settledHere = settled.identity === identity
  const status = enabled && settledHere ? settled.status : 'unknown'
  const error = settledHere ? settled.error : null

  const settle = useCallback((next, err = null) => {
    setSettled({ identity, status: next, error: err })
  }, [identity])

  // Initial status read whenever the subject identity changes (or the hook becomes
  // enabled). Only ever writes state from the async callbacks — `active` drops a late
  // response after the identity moved on.
  useEffect(() => {
    if (!enabled) return undefined
    let active = true
    fnsRef.current.fetchStatus()
      .then(r => {
        if (!active) return
        const next = r?.status || 'none'
        setSettled({
          identity,
          status: next,
          error: next === 'error' ? (r.message || failedMessage) : null,
        })
      })
      // A status read that fails should not strand the button inert: fall back to
      // offering a launch rather than leaving it permanently unusable.
      .catch(() => { if (active) setSettled({ identity, status: 'none', error: null }) })
    return () => { active = false }
  }, [enabled, identity, failedMessage])

  // While a run is in flight, keep asking until it reaches a terminal state.
  useEffect(() => {
    if (status !== 'generating') return undefined
    let active = true
    const timer = setInterval(() => {
      fnsRef.current.fetchStatus()
        .then(r => {
          if (!active || !r?.status) return
          setSettled({
            identity,
            status: r.status,
            error: r.status === 'error' ? (r.message || failedMessage) : null,
          })
        })
        // A blip mid-run is not a failed job — keep polling.
        .catch(() => {})
    }, pollMs)
    return () => { active = false; clearInterval(timer) }
  }, [status, identity, pollMs, failedMessage])

  /**
   * Start a run.
   *
   * Flips to 'generating' immediately rather than waiting for the status endpoint to
   * say so: the row that carries that status may not be written yet at launch, so a
   * poll fired right now could answer 'none' and re-offer a button the user just
   * pressed. The polling effect takes over from here.
   */
  const start = useCallback(async () => {
    setSettled({ identity, status: 'generating', error: null })
    try {
      await fnsRef.current.launch()
    } catch (e) {
      setSettled({ identity, status: 'error', error: e.detail || e.message || launchFailedMessage })
    }
  }, [identity, launchFailedMessage])

  return { status, error, start, settle }
}
